
import * as React from "react"
import { Area, AreaChart, Bar, CartesianGrid, XAxis, ComposedChart, Line, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts"

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/components/ui/toggle-group"
import { useIsMobile } from "@/hooks/use-mobile"
import { useGetDashboardChart } from "@/modules/dashboard/dashboard"
import { useEffect, useState, useMemo } from "react"
import { formatPrice } from "@/utils/number"
import { cn } from "@/lib/utils"

type ChartTooltipPayload = {
  value: number | string
  name: string
  color?: string
  dataKey?: string
  payload?: {
    buyKg?: number
    sellKg?: number
  }
}

const CustomChartTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: ChartTooltipPayload[]
  label?: string
}) => {
  if (!active || !payload?.length) return null

  const buy = Number(payload.find((item) => item.dataKey === "buy")?.value ?? 0)
  const sell = Number(payload.find((item) => item.dataKey === "sell")?.value ?? 0)
  const row = payload[0]?.payload as {
    buyKg?: number
    sellKg?: number
    expenses?: number
    net?: number
  }
  const buyKg = Number(row?.buyKg ?? 0)
  const sellKg = Number(row?.sellKg ?? 0)
  const expenses = Number(row?.expenses ?? 0)
  const netFromRow = row?.net
  const profitLoss =
    netFromRow !== undefined && !Number.isNaN(Number(netFromRow))
      ? Number(netFromRow)
      : sell - buy - expenses
  const netKg = sellKg - buyKg
  const isProfit = profitLoss >= 0
  const isProfitKg = netKg >= 0

  return (
    <div className="rounded-2xl border-none bg-white p-4 shadow-lg">
      <p className="mb-2 text-base font-bold text-slate-800">{label}</p>
      {payload.map((item) => (
        <p key={`${item.dataKey}-${item.name}`} className="mb-1 text-sm font-semibold" style={{ color: item.color }}>
          {item.name.includes("บาท")
            ? `${item.name}: ${formatPrice(Number(item.value))}`
            : `${item.name}: ${formatPrice(Number(item.value))} บิล`}
        </p>
      ))}
      <p className={cn("mt-2 border-t pt-2 text-sm font-bold", isProfit ? "text-emerald-600" : "text-rose-600")}>
        กำไรสุทธิ (บาท): {isProfit ? "+" : "-"}{formatPrice(Math.abs(profitLoss))}
      </p>
      {expenses > 0 && (
        <p className="mt-1 text-xs text-slate-600">ค่าใช้จ่ายในวันนั้น: {formatPrice(expenses)} บาท</p>
      )}
      <p className={cn("mt-1 text-sm font-bold", isProfitKg ? "text-emerald-600" : "text-rose-600")}>
        น้ำหนักเกิน/ขาด (กก.): {isProfitKg ? "+" : "-"}{Math.abs(netKg).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </p>
    </div>
  )
}

export function ChartAreaInteractive({ reportLocation }: { reportLocation?: string }) {
  const isMobile = useIsMobile()
  const [timeRange, setTimeRange] = useState("7d")

  const { data: chartDataResponse } = useGetDashboardChart({ timeRange: timeRange, location: reportLocation })

  useEffect(() => {
    if (isMobile) {
      setTimeRange("7d")
    }
  }, [isMobile])

  const { chartData, buyPercent, sellPercent, expensesPercent, currentBuy, currentSell, currentExpenses } =
    useMemo(() => {
    const rawData = chartDataResponse?.data || []

    const processedData = rawData.map((item) => ({
      ...item,
      buy: Number(item.buy) || 0,
      sell: Number(item.sell) || 0,
      expenses: Number(item.expenses) || 0,
      buyCount: Number(item.buyCount) || 0,
      sellCount: Number(item.sellCount) || 0,
    }))

    const current = processedData[processedData.length - 1] || { buy: 0, sell: 0, expenses: 0 }
    const previous = processedData[processedData.length - 2] || { buy: 0, sell: 0, expenses: 0 }

    const calcPercent = (curr: number, prev: number) => {
      if (!prev || prev === 0) return curr > 0 ? 100 : 0
      return ((curr - prev) / prev) * 100
    }

    return {
      chartData: processedData,
      buyPercent: calcPercent(Number(current.buy), Number(previous.buy)),
      sellPercent: calcPercent(Number(current.sell), Number(previous.sell)),
      expensesPercent: calcPercent(Number(current.expenses), Number(previous.expenses)),
      currentBuy: current.buy,
      currentSell: current.sell,
      currentExpenses: current.expenses,
    }
  }, [chartDataResponse])

  const renderTrend = (value: number) => {
    const isPositive = value > 0;
    const isNeutral = value === 0;
    return (
      <span className={cn(
        "text-xs font-bold px-2 py-1 rounded-full",
        isPositive ? "bg-secondary text-primary" : isNeutral ? "bg-muted text-muted-foreground" : "bg-red-100 text-red-700"
      )}>
        {isPositive ? '+' : ''}{value.toFixed(1)}%
      </span>
    )
  }

  return (
    <Card className="flex flex-col border-none shadow-sm rounded-[2rem] w-full p-2 min-w-0">
      <CardHeader className="flex flex-col md:flex-row md:items-start md:justify-between w-full pb-0 mb-4">
        <div className="flex flex-col gap-2">
          <div>
            <CardTitle className="text-2xl font-bold tracking-tight mb-1">ยอดซื้อขาย</CardTitle>
            <CardDescription className="text-sm font-medium text-gray-500">
              ปริมาณการซื้อเข้า ขายออก และค่าใช้จ่ายตามวันที่ (รายวัน)
            </CardDescription>
          </div>
          <div className="mt-2 flex flex-wrap gap-4 gap-y-3 sm:gap-6">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-bold text-gray-400 tracking-wider">ล่าสุด (ซื้อเข้า)</span>
              <div className="flex items-center gap-2">
                <span className="text-xl font-extrabold text-red-600">{formatPrice(currentBuy)}</span>
                {renderTrend(buyPercent)}
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-bold text-gray-400 tracking-wider">ล่าสุด (ขายออก)</span>
              <div className="flex items-center gap-2">
                <span className="text-xl font-extrabold text-blue-600">{formatPrice(currentSell)}</span>
                {renderTrend(sellPercent)}
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-bold tracking-wider text-gray-400">ล่าสุด (ค่าใช้จ่าย)</span>
              <div className="flex items-center gap-2">
                <span className="text-xl font-extrabold text-amber-600">{formatPrice(currentExpenses)}</span>
                {renderTrend(expensesPercent)}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2 mt-4 md:mt-0">
          <ToggleGroup
            type="single"
            value={timeRange}
            onValueChange={setTimeRange}
            variant="outline"
            className="md:flex hidden bg-slate-50 p-1 rounded-xl"
          >
            <ToggleGroupItem value="1y" className="h-8 px-4 rounded-lg data-[state=on]:bg-white data-[state=on]:shadow-sm">
              1 ปี
            </ToggleGroupItem>
            <ToggleGroupItem value="6m" className="h-8 px-4 rounded-lg data-[state=on]:bg-white data-[state=on]:shadow-sm">
              6 เดือน
            </ToggleGroupItem>
            <ToggleGroupItem value="3m" className="h-8 px-4 rounded-lg data-[state=on]:bg-white data-[state=on]:shadow-sm">
              3 เดือน
            </ToggleGroupItem>
            <ToggleGroupItem value="1m" className="h-8 px-4 rounded-lg data-[state=on]:bg-white data-[state=on]:shadow-sm">
              1 เดือน
            </ToggleGroupItem>
            <ToggleGroupItem value="7d" className="h-8 px-4 rounded-lg data-[state=on]:bg-white data-[state=on]:shadow-sm">
              7 วัน
            </ToggleGroupItem>
          </ToggleGroup>
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="md:hidden flex w-40 rounded-xl" aria-label="เลือกระยะเวลา">
              <SelectValue placeholder="ระยะเวลา" />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              <SelectItem value="1y" className="rounded-lg">1 ปี</SelectItem>
              <SelectItem value="1m" className="rounded-lg">1 เดือน</SelectItem>
              <SelectItem value="7d" className="rounded-lg">7 วัน</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      
      <CardContent className="px-2 pt-4 sm:px-6 w-full mt-4 min-w-0">
        <div className="h-[400px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
              <XAxis
                dataKey="name"
                tickLine={false}
                axisLine={false}
                tickMargin={10}
                tick={{ fill: '#9CA3AF', fontSize: 12 }}
              />
              {/* Left YAxis for Volumn (บาท) */}
              <YAxis 
                yAxisId="left"
                width={65}
                tickLine={false} 
                axisLine={false} 
                tickMargin={10} 
                tick={{ fill: '#9CA3AF', fontSize: 12 }}
                tickFormatter={(value) => `${value >= 1000000 ? (value / 1000000).toFixed(1) + ' ล้าน' : value >= 1000 ? (value / 1000).toFixed(1) + ' พัน' : value}`}
              />
              {/* Right YAxis for Count (Items) */}
              <YAxis 
                yAxisId="right"
                width={65}
                orientation="right"
                tickLine={false} 
                axisLine={false} 
                tickMargin={10} 
                tick={{ fill: '#9CA3AF', fontSize: 12 }}
                tickFormatter={(value) => `${value.toLocaleString()} บิล`}
              />
              <Tooltip
                cursor={{ fill: '#F3F4F6' }}
                contentStyle={{ borderRadius: '1rem', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)' }}
                content={<CustomChartTooltip />}
              />
              <Legend iconType="circle" wrapperStyle={{ paddingTop: '20px' }} />
              
              {/* Weight (บาท) mapped to left axis */}
              <Bar yAxisId="left" dataKey="buy" name="ยอดซื้อเข้า (บาท)" fill="#ef4444" radius={[4, 4, 0, 0]} barSize={24} />
              <Bar yAxisId="left" dataKey="sell" name="ยอดขายออก (บาท)" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={24} />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="expenses"
                name="ค่าใช้จ่าย (บาท)"
                stroke="#f59e0b"
                strokeWidth={3}
                dot={{ r: 4, strokeWidth: 2, fill: '#f59e0b' }}
                activeDot={{ r: 6 }}
              />

              {/* Counts mapped to right axis */}
              <Line yAxisId="right" type="monotone" dataKey="buyCount" name="จำนวนบิลซื้อ (รายการ)" stroke="#f87171" strokeWidth={3} dot={{ r: 4, strokeWidth: 2 }} activeDot={{ r: 6 }} />
              <Line yAxisId="right" type="monotone" dataKey="sellCount" name="จำนวนบิลขาย (รายการ)" stroke="#60a5fa" strokeWidth={3} dot={{ r: 4, strokeWidth: 2 }} activeDot={{ r: 6 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  )
}
